import * as React from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import DownloadIcon from "@mui/icons-material/Download";

import { useApiClient } from "@/hooks/useApiClient";
import { getOwnerAnalyzeFileApi } from "@/features/ownerInfo/api";
import { toast } from "react-toastify";
import { type OwnerAnalyzeFileDownloadRequest } from "@/features/ownerInfo/types";
import { DmMaxBranchNumberModal } from "@/features/ownerInfo/components/DmMaxBranchNumberModal";
import { PropertyDetailOptionModal } from "@/components/PropertyDetailOptionModal";
import { HttpStatusCode, isAxiosError } from "axios";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";

interface OutputMenuProps {
  disabled: boolean;
  requestId: number;
  taskId?: number;
  identificationName?: string;
  showDesignExcel?: boolean;
  isReadOnly?: boolean;
  isPropertyDetailOptionCredit?: boolean;
  isDmCredit?: boolean;
  onDownloadComplete?: () => void;
}

export default function OutputMenu({
  disabled,
  requestId,
  taskId,
  identificationName,
  showDesignExcel = true,
  isReadOnly = false,
  isPropertyDetailOptionCredit = false,
  isDmCredit = false,
  onDownloadComplete,
}: OutputMenuProps): React.ReactElement {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const [openDmMaxBranchNumberModal, setOpenDmMaxBranchNumberModal] =
    React.useState(false);
  const [realEstateOwnerAnalyzeRequestId, setRealEstateOwnerAnalyzeRequestId] =
    React.useState<number>(NaN);
  const [openPropertyDetailOptionModal, setOpenPropertyDetailOptionModal] =
    React.useState(false);

  const { apiClient } = useApiClient();
  const isReadOnlyPropertyDetailBlocked =
    isReadOnly && !isPropertyDetailOptionCredit;
  const isReadOnlyDmBlocked = isReadOnly && !isDmCredit;

  const buildPropertyDetailFileName = (): string => {
    const resolvedTaskId = taskId ?? requestId;
    const resolvedName = identificationName?.trim();
    const nameSuffix = resolvedName ? `_${resolvedName}` : "";
    return `【物件名称・物件住所付き】所有者事項解析結果_${resolvedTaskId}${nameSuffix}.xlsx`;
  };

  const buttonClickHandler = async ({
    requestId,
    resType,
    fallBackName,
  }: OwnerAnalyzeFileDownloadRequest): Promise<void> => {
    if (resType === "excel_direct_mail") {
      setRealEstateOwnerAnalyzeRequestId(requestId);
      setOpenDmMaxBranchNumberModal(true);
    } else {
      try {
        await getOwnerAnalyzeFileApi(apiClient, {
          requestId,
          resType,
          fallBackName,
        });
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.Forbidden
        ) {
          toast.error(READ_ONLY_TOOLTIP_MESSAGE);
          throw error;
        }
        toast.error("ファイルのダウンロードに失敗しました");
        throw error;
      }
    }
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        disabled={disabled}
        variant="outlined"
        color="primary"
        id="output-button"
        aria-controls={open ? "output-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        出力
        <DownloadIcon />
      </Button>
      <Menu
        id="output-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "output-button",
        }}
      >
        <MenuItem
          onClick={() => {
            void buttonClickHandler({
              requestId,
              resType: "excel",
              fallBackName: `所有者事項解析結果_${requestId}.xlsx`,
            }).catch(() => undefined);
            handleClose();
          }}
        >
          エクセルダウンロード
        </MenuItem>
        {showDesignExcel && (
          <MenuItem
            onClick={() => {
              void buttonClickHandler({
                requestId,
                resType: "excel_design",
                fallBackName: `【色付き】所有者事項解析結果_${requestId}.xlsx`,
              }).catch(() => undefined);
              handleClose();
            }}
          >
            エクセルダウンロード（色付き）
          </MenuItem>
        )}
        <ReadOnlyTooltip isReadOnly={isReadOnlyPropertyDetailBlocked}>
          <MenuItem
            disabled={isReadOnlyPropertyDetailBlocked}
            onClick={() => {
              if (isPropertyDetailOptionCredit) {
                // 過去に一度実行済みの場合は直接ダウンロード
                void buttonClickHandler({
                  requestId,
                  resType: "excel_property",
                  fallBackName: buildPropertyDetailFileName(),
                })
                  .then(() => {
                    onDownloadComplete?.();
                  })
                  .catch(() => undefined);
                handleClose();
              } else {
                // 初回の場合はモーダルを表示
                setOpenPropertyDetailOptionModal(true);
                handleClose();
              }
            }}
          >
            エクセルダウンロード（物件名称・物件住所付き）
          </MenuItem>
        </ReadOnlyTooltip>
        <MenuItem
          onClick={() => {
            void buttonClickHandler({
              requestId,
              resType: "csv_owners",
              fallBackName: `所有者事項解析結果_所有者ベース_${requestId}.csv`,
            }).catch(() => undefined);
            handleClose();
          }}
        >
          所有者ベースCSVダウンロード
        </MenuItem>
        <MenuItem
          onClick={() => {
            void buttonClickHandler({
              requestId,
              resType: "csv_estates",
              fallBackName: `所有者事項解析結果_登記件数ベース_${requestId}.csv`,
            }).catch(() => undefined);
            handleClose();
          }}
        >
          登記件数ベースCSVダウンロード
        </MenuItem>
        <ReadOnlyTooltip isReadOnly={isReadOnlyDmBlocked}>
          <MenuItem
            disabled={isReadOnlyDmBlocked}
            onClick={() => {
              void buttonClickHandler({
                requestId,
                resType: "excel_direct_mail",
                fallBackName: `DM対象者リスト（案）_${requestId}.xlsx`,
              }).catch(() => undefined);
              handleClose();
            }}
          >
            DM対象者リスト（案）
          </MenuItem>
        </ReadOnlyTooltip>
      </Menu>
      <DmMaxBranchNumberModal
        open={openDmMaxBranchNumberModal}
        setOpen={setOpenDmMaxBranchNumberModal}
        requestId={realEstateOwnerAnalyzeRequestId}
        fallBackName={`DM対象者リスト（案）_${realEstateOwnerAnalyzeRequestId}.xlsx`}
      />
      <PropertyDetailOptionModal
        open={openPropertyDetailOptionModal}
        onClose={() => {
          setOpenPropertyDetailOptionModal(false);
        }}
        onConfirm={async () => {
          await buttonClickHandler({
            requestId,
            resType: "excel_property",
            fallBackName: buildPropertyDetailFileName(),
          });
          onDownloadComplete?.();
        }}
        target="owner"
      />
    </div>
  );
}
